import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import MobileActionMenu from "../../../components/MobileActionMenu";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";

import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import SendIcon from "@material-ui/icons/Send";
import ScheduleIcon from "@material-ui/icons/Schedule";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import PosterModal from "../../../components/PosterModal";
import ConfirmationModal from "../../../components/ConfirmationModal";
import TableRowSkeleton from "../../../components/TableRowSkeleton";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { useDate } from "../../../hooks/useDate";

const reducer = (state, action) => {
  if (action.type === "LOAD_POSTS") {
    const posts = action.payload;
    const newPosts = [];
    posts.forEach(post => {
      const idx = state.findIndex(p => p.id === post.id);
      if (idx !== -1) state[idx] = post;
      else newPosts.push(post);
    });
    return [...state, ...newPosts];
  }
  if (action.type === "UPDATE_POST") {
    const post = action.payload;
    const idx = state.findIndex(p => p.id === post.id);
    if (idx !== -1) { state[idx] = post; return [...state]; }
    return [post, ...state];
  }
  if (action.type === "DELETE_POST") {
    return state.filter(p => p.id !== action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "transparent",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid #e0e0e0",
    flexWrap: "wrap",
    gap: 12,
    [theme.breakpoints.down("sm")]: { padding: "12px 16px", flexDirection: "column", alignItems: "stretch" },
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  headerTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#1a1a1a",
    [theme.breakpoints.down("sm")]: { fontSize: "1.2rem" },
  },
  headerSubtitle: {
    fontSize: "0.875rem",
    color: "#666",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "stretch" },
  },
  searchField: {
    backgroundColor: "#fff",
    borderRadius: 8,
    minWidth: 220,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#1976d2" },
    },
    [theme.breakpoints.down("sm")]: { minWidth: "unset", width: "100%" },
  },
  addButton: {
    borderRadius: 8,
    padding: "6px 20px",
    textTransform: "none",
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: { width: "100%" },
  },
  content: {
    padding: "16px 24px",
    [theme.breakpoints.down("sm")]: { padding: "12px 12px" },
  },
  tableWrapper: {
    backgroundColor: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    [theme.breakpoints.down("sm")]: { overflowX: "auto" },
  },
  tableHead: {
    backgroundColor: "var(--sidebar-color, #1e293b)",
    "& th": {
      color: "#cbd5e1",
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      borderBottom: "none",
      padding: "14px 16px",
    },
  },
  tableBody: {
    "& td": {
      padding: "12px 16px",
      fontSize: "0.875rem",
      color: "#334155",
      borderBottom: "1px solid #f1f5f9",
    },
    "& tr:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    color: "#999",
  },
  platformChips: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
  },
  chipWhatsapp: {
    backgroundColor: "#25D366",
    color: "#fff",
    height: 22,
    fontSize: 11,
    fontWeight: 600,
  },
  chipInstagram: {
    background: "linear-gradient(45deg, #405de6, #e1306c)",
    color: "#fff",
    height: 22,
    fontSize: 11,
    fontWeight: 600,
  },
  chipFacebook: {
    backgroundColor: "#1877F2",
    color: "#fff",
    height: 22,
    fontSize: 11,
    fontWeight: 600,
  },
  scheduledBadge: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    color: "#64748b",
  },
}));

const STATUS_CONFIG = {
  draft:      { label: "Rascunho",   bg: "#f5f5f5",  color: "#9e9e9e" },
  scheduled:  { label: "Agendado",   bg: "#fffbeb",  color: "#d97706" },
  publishing: { label: "Publicando", bg: "#eef2ff",  color: "#4338ca" },
  published:  { label: "Publicado",  bg: "#f0fdf4",  color: "#16a34a" },
  partial:    { label: "Parcial",    bg: "#fff7ed",  color: "#ea580c" },
  failed:     { label: "Falhou",     bg: "#fef2f2",  color: "#dc2626" },
};

// Tipos específicos de post → chip na tabela
const POST_TYPE_LABELS = {
  whatsapp_status:  { label: "WA Status",         chipClass: "chipWhatsapp" },
  instagram_feed:   { label: "IG Feed",            chipClass: "chipInstagram" },
  instagram_story:  { label: "IG Story",           chipClass: "chipInstagram" },
  facebook_feed:    { label: "FB Feed",            chipClass: "chipFacebook" },
  facebook_story:   { label: "FB Story",           chipClass: "chipFacebook" },
};

const Poster = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sidebarColor = theme.palette.primary.main || "#1e293b";
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const [posts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingPost, setDeletingPost] = useState(null);
  const [publishing, setPublishing] = useState(null);

  useEffect(() => {
    dispatch({ type: "RESET" });
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => fetchPosts(), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam]);

const fetchPosts = async () => {
    try {
      const { data } = await api.get("/social-posts");
      const filtered = searchParam
        ? (data.posts || []).filter(p =>
            (p.title || "").toLowerCase().includes(searchParam) ||
            (p.content || "").toLowerCase().includes(searchParam)
          )
        : (data.posts || []);
      dispatch({ type: "LOAD_POSTS", payload: filtered });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

const handleSearch = (e) => {
    setSearchParam(e.target.value.toLowerCase());
  };

  const handleOpenNew = () => {
    setSelectedPost(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (post) => {
    setSelectedPost(post);
    setModalOpen(true);
  };

  const handleModalSuccess = (post, isEdit) => {
    dispatch({ type: "UPDATE_POST", payload: post });
  };

  const handlePublish = async (post) => {
    setPublishing(post.id);
    try {
      const { data } = await api.post(`/social-posts/${post.id}/publish`);
      dispatch({ type: "UPDATE_POST", payload: data });
      const msgs = {
        published: "Publicado com sucesso!",
        partial: "Publicado parcialmente — verifique os resultados",
        failed: "Falhou ao publicar — verifique as conexões",
      };
      toast[data.status === "published" ? "success" : "warning"](msgs[data.status] || "Publicado");
    } catch (err) {
      toastError(err);
    } finally {
      setPublishing(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPost) return;
    try {
      const { data } = await api.delete(`/social-posts/${deletingPost.id}`);
      dispatch({ type: "DELETE_POST", payload: deletingPost.id });
      if (data?.whatsappError) {
        toast.warning(`Removido do sistema, mas não foi possível deletar do WhatsApp: ${data.whatsappError}`);
      } else if (data?.deletedFromWhatsApp) {
        toast.success("Post removido do sistema e do WhatsApp!");
      } else {
        toast.success("Post removido com sucesso!");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingPost(null);
      setConfirmDeleteOpen(false);
    }
  };

  const getStatusChip = (status) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    return (
      <Chip
        label={cfg.label}
        size="small"
        style={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, height: 22, fontSize: 11 }}
      />
    );
  };

  return (
    <Box
      className={classes.root}
      style={{ "--sidebar-color": sidebarColor }}
    >
      <ConfirmationModal
        title={deletingPost ? `Excluir "${deletingPost.title}"?` : "Excluir post?"}
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Esta ação não poderá ser desfeita.
      </ConfirmationModal>

      <PosterModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        post={selectedPost}
        onSuccess={handleModalSuccess}
      />

      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Box>
            <Typography className={classes.headerTitle}>
              Poster — Status & Redes Sociais
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {posts.length} {posts.length === 1 ? "post cadastrado" : "posts cadastrados"}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.headerRight}>
          <TextField
            placeholder="Buscar posts..."
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            className={classes.addButton}
            onClick={handleOpenNew}
          >
            Novo Post
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box className={classes.content}>
        <Box className={classes.tableWrapper}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell align="center">Plataformas</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Agendado para</TableCell>
                <TableCell align="center">Publicado em</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {loading ? (
                <TableRowSkeleton columns={6} />
              ) : posts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box className={classes.emptyState}>
                      <Typography>Nenhum post encontrado</Typography>
                      <Typography variant="caption">
                        {searchParam ? "Tente outro termo de busca" : 'Clique em "Novo Post" para começar'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                posts.map(post => (
                  <TableRow key={post.id} style={{ cursor: "pointer" }} onClick={() => handleOpenEdit(post)}>
                    <TableCell>
                      <Typography style={{ fontWeight: 600, color: "#1976d2", fontSize: 13 }}>
                        {post.title}
                      </Typography>
                      {post.content && (
                        <Typography
                          variant="caption"
                          style={{ color: "#999", display: "block", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {post.content}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Box className={classes.platformChips} justifyContent="center">
                        {(post.platforms || []).map(p => {
                          const cfg = POST_TYPE_LABELS[p];
                          if (!cfg) return null;
                          return (
                            <Chip
                              key={p}
                              label={cfg.label}
                              size="small"
                              className={classes[cfg.chipClass]}
                            />
                          );
                        })}
                      </Box>
                    </TableCell>

                    <TableCell align="center">{getStatusChip(post.status)}</TableCell>

                    <TableCell align="center">
                      {post.scheduledAt ? (
                        <Box className={classes.scheduledBadge} justifyContent="center">
                          <ScheduleIcon style={{ fontSize: 14, color: "#d97706" }} />
                          {datetimeToClient(post.scheduledAt)}
                        </Box>
                      ) : "—"}
                    </TableCell>

                    <TableCell align="center">
                      {post.publishedAt ? datetimeToClient(post.publishedAt) : "—"}
                    </TableCell>

                    <TableCell align="center" onClick={e => e.stopPropagation()}>
                      {isMobile ? (
                        <MobileActionMenu actions={[
                          { label: "Publicar agora", icon: <SendIcon fontSize="small" />, onClick: () => handlePublish(post), disabled: ["publishing","published"].includes(post.status) || publishing === post.id },
                          { label: "Editar", icon: <EditIcon fontSize="small" />, onClick: () => handleOpenEdit(post) },
                          { label: "Excluir", icon: <DeleteOutlineIcon fontSize="small" />, onClick: () => { setDeletingPost(post); setConfirmDeleteOpen(true); }, color: "#ef4444" },
                        ]} />
                      ) : (
                        <>
                          <Tooltip title="Publicar agora">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handlePublish(post)}
                                disabled={["publishing", "published"].includes(post.status) || publishing === post.id}
                                style={{ color: "#25D366" }}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleOpenEdit(post)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              onClick={() => { setDeletingPost(post); setConfirmDeleteOpen(true); }}
                              style={{ color: "#ef4444" }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default Poster;
